import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getColors, fonts } from '../theme';
import { useApp } from '../context/AppContext';
import Logo from '../components/Logo';
import { devError } from '../utils/debugLogger';
import { FUNCTION_URLS } from '../config/functions';
import { 
  CheckSquare, Target, Calendar, Sparkles, 
  Mail, Send, Heart, Shield, Zap, ArrowLeft, Check
} from 'lucide-react';

const AboutPage = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const values = [
    {
      icon: Zap,
      title: 'Simplicity First',
      description: 'No clutter, no distractions. Just the tools you need to build better habits.'
    },
    {
      icon: Shield,
      title: 'Privacy Focused',
      description: 'Your data belongs to you. We believe in transparency and user control.'
    },
    {
      icon: Heart,
      title: 'Mindful Design',
      description: 'Every feature is intentionally crafted to support your wellbeing, not exploit it.'
    }
  ];

  const features = [
    { icon: CheckSquare, title: 'Habit Tracking', description: 'Build consistent daily routines' },
    { icon: Target, title: 'Goal Setting', description: 'SMART goals with milestones' },
    { icon: Calendar, title: 'Calendar Integration', description: 'Sync with Google Calendar' },
    { icon: Sparkles, title: 'Task Management', description: 'Prioritize what matters' }
  ];

  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Send to Firebase Function
      const response = await fetch(FUNCTION_URLS.contact.sendEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      devError('Error submitting form:', error);
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg }}>
      {/* Navigation */}
      <nav className="px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <Logo size="md" isDark={isDark} />
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all hover:opacity-80"
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-4xl mx-auto text-center">
        <h1 
          className="text-4xl md:text-5xl lg:text-6xl mb-6"
          style={{ color: colors.textPrimary, fontWeight: 600, letterSpacing: '-0.035em' }}
        >
          About mind.
        </h1>
        <p 
          className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          style={{ color: colors.textSecondary }}
        >
          A minimalist approach to personal productivity. We believe that lasting change 
          comes from intentional, mindful habits, not overwhelming to-do lists.
        </p>
      </section>

      {/* Our Story */}
      <section className="px-6 py-16 max-w-4xl mx-auto">
        <div 
          className="pt-5 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <h2 
            className="text-2xl md:text-3xl mb-6 font-semibold tracking-[-0.035em]"
            style={{ color: colors.textPrimary }}
          >
            Our Story
          </h2>
          <div className="space-y-4" style={{ color: colors.textSecondary }}>
            <p className="leading-relaxed">
              mind. was born from a simple frustration: most productivity apps are designed 
              to keep you busy, not to help you grow. They pile on features, notifications, 
              and gamification tricks that often leave you feeling more overwhelmed than organized.
            </p>
            <p className="leading-relaxed">
              We wanted something different: a calm, focused space where you could track 
              what truly matters without the noise. A tool that respects your time and 
              attention, helping you build sustainable habits rather than temporary bursts 
              of productivity.
            </p>
            <p className="leading-relaxed">
              That's why we built mind. with intention at its core. Every feature exists 
              for a reason. Every design decision prioritizes your wellbeing over engagement 
              metrics. Because we believe the best productivity tool is one that eventually 
              helps you need it less.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <h2 
          className="text-2xl md:text-3xl text-center mb-12 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          What We Believe
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {values.map((value, idx) => (
            <div 
              key={idx}
              className="pt-5 pb-2 text-center"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${colors.accent}20` }}
              >
                <value.icon size={24} style={{ color: colors.accent }} />
              </div>
              <h3 
                className="text-lg font-medium mb-2"
                style={{ color: colors.textPrimary }}
              >
                {value.title}
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Overview */}
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <h2 
          className="text-2xl md:text-3xl text-center mb-12 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          What You Can Do
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="pt-5 pb-2"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <feature.icon size={20} style={{ color: colors.accent }} className="mb-3" />
              <h3 
                className="font-medium mb-1"
                style={{ color: colors.textPrimary }}
              >
                {feature.title}
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="px-6 py-16 max-w-2xl mx-auto">
        <div 
          className="pt-5 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${colors.accent}20` }}
            >
              <Mail size={20} style={{ color: colors.accent }} />
            </div>
            <h2 
              className="text-2xl font-semibold tracking-[-0.035em]"
              style={{ color: colors.textPrimary }}
            >
              Get in Touch
            </h2>
          </div>
          
          <p className="mb-8" style={{ color: colors.textSecondary }}>
            Have a question, feedback, or just want to say hello? We'd love to hear from you.
          </p>

          {submitted ? (
            <div 
              className="text-center py-12 rounded-lg"
              style={{ backgroundColor: `${colors.success}10` }}
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${colors.success}20` }}
              >
                <Check size={32} style={{ color: colors.success }} />
              </div>
              <h3 
                className="text-xl font-medium mb-2"
                style={{ color: colors.textPrimary }}
              >
                Message Sent!
              </h3>
              <p style={{ color: colors.textSecondary }}>
                Thanks for reaching out. We'll get back to you soon.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 px-6 py-2 rounded-lg text-sm"
                style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-xs mb-2 font-medium"
                    style={{ color: colors.textMuted }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-md outline-none transition-all focus:ring-2"
                    style={{ 
                      backgroundColor: colors.bg, 
                      color: colors.textPrimary, 
                      border: `1px solid ${colors.border}`,
                      focusRingColor: colors.accent
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block text-xs mb-2 font-medium"
                    style={{ color: colors.textMuted }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-md outline-none transition-all focus:ring-2"
                    style={{ 
                      backgroundColor: colors.bg, 
                      color: colors.textPrimary, 
                      border: `1px solid ${colors.border}`
                    }}
                  />
                </div>
              </div>
              
              <div>
                <label 
                  className="block text-xs mb-2 font-medium"
                  style={{ color: colors.textMuted }}
                >
                  Subject
                </label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 rounded-md outline-none"
                  style={{ 
                    backgroundColor: colors.bg, 
                    color: colors.textPrimary, 
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <option value="">Select a topic</option>
                  <option value="general">General Inquiry</option>
                  <option value="feedback">Product Feedback</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                  <option value="billing">Billing Question</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label 
                  className="block text-xs mb-2 font-medium"
                  style={{ color: colors.textMuted }}
                >
                  Message *
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us what's on your mind..."
                  className="w-full px-4 py-3 rounded-md outline-none resize-none"
                  style={{ 
                    backgroundColor: colors.bg, 
                    color: colors.textPrimary, 
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              {/* Error Message */}
              {submitError && (
                <div 
                  className="p-3 rounded-lg text-sm"
                  style={{ backgroundColor: `${colors.error}20`, color: colors.error, border: `1px solid ${colors.error}` }}
                >
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="px-6 py-12"
      >
        <div className="max-w-4xl mx-auto text-center">
          <Logo size="sm" isDark={isDark} className="mx-auto mb-4" />
          <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
            Build better habits. Achieve your goals.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <Link 
              to="/privacy" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: colors.textSecondary }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: colors.textSecondary }}
            >
              Terms of Service
            </Link>
          </div>
          <p className="mt-8 text-xs" style={{ color: colors.textMuted }}>
            © {new Date().getFullYear()} Mill Creek Media LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
